import type { RegistrationComparison } from '@/lib/registration-comparison'

export type ChartSeries = RegistrationComparison['chart']['current']
export type ChartData = {
  current: ChartSeries
  previous: ChartSeries | null
  currentLabel: string
  previousLabel: string
  todayDay: number | null
}

export function chartData(report: RegistrationComparison): ChartData {
  return {
    ...report.chart,
    currentLabel: report.event.starts_on?.slice(0, 4) ?? 'This event',
    previousLabel: report.previous_event?.starts_on?.slice(0, 4) ?? 'Previous edition',
    todayDay: report.mode === 'pacing' && report.days_out !== null ? -report.days_out : null,
  }
}

export function chartRows({ current, previous }: ChartData) {
  const currentByDay = new Map(current.map((point) => [point.day, point.value]))
  const previousByDay = new Map(previous?.map((point) => [point.day, point.value]))
  const days = new Set([...currentByDay.keys(), ...previousByDay.keys()])
  return [...days]
    .sort((a, b) => a - b)
    .map((day) => ({
      day,
      current: currentByDay.get(day),
      previous: previousByDay.get(day),
    }))
}

export function chartValue(value: number | undefined) {
  return value === undefined ? '—' : String(value)
}

// Chart days run from the past (negative) to the start day (0). Every surface that names a day
// (axis tick, table cell, tooltip) words it as a positive count of days before the event starts.
export function daysOutLabel(day: number) {
  return String(-day)
}

function dayWording(day: number) {
  if (day === 0) return 'Event day'
  return `${daysOutLabel(day)} ${day === -1 ? 'day' : 'days'} before`
}

export function pointTitle(row: ReturnType<typeof chartRows>[number], data: ChartData) {
  const dayLabel = dayWording(row.day)
  return `${dayLabel} · ${data.currentLabel}: ${chartValue(row.current)}${
    data.previous ? ` · ${data.previousLabel}: ${chartValue(row.previous)}` : ''
  }`
}

export function chartScale(data: ChartData, width: number) {
  const points = [...data.current, ...(data.previous ?? [])]
  const first = Math.min(-1, ...points.map((point) => point.day))
  const last = Math.max(0, ...points.map((point) => point.day))
  const max = Math.max(1, ...points.map((point) => point.value))
  const magnitude = 10 ** Math.floor(Math.log10(max / 5))
  const step = Math.max(1, [1, 2, 5, 10].find((part) => part * magnitude >= max / 5)! * magnitude)
  const top = Math.ceil(max / step) * step
  return {
    x: (day: number) => 44 + ((day - first) / (last - first)) * (width - 60),
    y: (value: number) => 160 - (value / top) * 148,
    ticks: Array.from({ length: top / step + 1 }, (_, index) => index * step),
    days: [
      ...new Set(
        Array.from({ length: 5 }, (_, index) => Math.round(first + ((last - first) * index) / 4)),
      ),
    ],
  }
}

export type ChartScale = ReturnType<typeof chartScale>

export function seriesPath(series: ChartSeries, scale: ChartScale) {
  return series
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'} ${scale.x(point.day).toFixed(1)} ${scale.y(point.value).toFixed(1)}`,
    )
    .join(' ')
}
